import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, retry, timeoutWith} from 'rxjs/operators';
import {ToastService} from '../toast-service';
import {Injectable, Injector} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {CrystalAvailabilityService} from '../crystal-availability.service';
import {UserInfoStorageService} from '../auth/user-info-storage.service';
import {CrystalClearFrontUrl} from '../../shared/pages/crystal-clear-front-url';

@Injectable({providedIn: 'root'})
export class HttpErrorInterceptor implements HttpInterceptor {

  constructor(private injector: Injector, toastService: ToastService,
              private translateService: TranslateService,
              private router: Router,
              private crystalAvailabilityService: CrystalAvailabilityService) {
    HttpErrorInterceptor.toastService = toastService;
    this.crystalAvailabilityService.isCrystalClearAvailableEmitter.subscribe(isAvailable => this.isCrystalClearAvailable = isAvailable);
  }

  private static toastService: ToastService;

  private isCrystalClearAvailable: boolean = true;

  timestamp: any;
  message: string = '';
  debugMessage: string | undefined;
  cause: string | undefined;

  private knownErrors = ['user_bad_credentials', 'user_locked', 'user_disabled', 'user_expired', 'user_have_to_change_password', 'access_is_denied'];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isCrystalClearAvailable && !request.url.includes('/api/health/ping')) {
      return new Observable<HttpEvent<any>>();
    }
    return next.handle(request)
      .pipe(timeoutWith(60000, throwError(new HttpErrorResponse({error: 'Timeout Error', status: 0}))),
        retry(0),
        catchError((error: HttpErrorResponse) => {

          let errorMessage: string;
          if (error.error instanceof ErrorEvent) {
            // client-side error
            errorMessage = `Error: ${error.error.message}`;
          } else {
            // server-side error
            errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
          }
          const errorBody = error.error && typeof error.error === 'object' ? error.error : {};
          this.message = errorBody.message || error.message || '';
          this.debugMessage = errorBody.debugMessage;
          this.cause = errorBody.cause;
          const isWarning = ['user_bad_credentials', 'user_locked', 'user_disabled', 'user_expired', 'user_have_to_change_password'].includes(this.message);
          if (this.knownErrors.includes(this.message)) {
            this.message = this.translateService.instant('error.message.' + this.message);
          } else if (error.status === 400 && errorBody.debugMessage === undefined) {
            this.message = this.message + ' ' + this.translateService.instant('interceptor.contact-support');
            this.debugMessage = error.message.toString();
            this.cause = JSON.stringify(error.error);
          }

          const serviceConnectionIssue: boolean = this.message.startsWith('java.net.ConnectException: ConnectException');
          const isUnauthorized: boolean = error.status === 401 || this.message == 'Unauthorized' || this.message == 'HTTP 401 Unauthorized';
          const isForbidden: boolean = error.status === 403 || this.message == 'Forbidden';

          if (error.status === 0) {
            this.crystalAvailabilityService.checkAvailability();
          } else if (serviceConnectionIssue) {
            this.crystalAvailabilityService.checkServicesAvailability();
            // this.router.navigateByUrl("")
          } else if (isUnauthorized) {
            // Session is no longer valid server-side (e.g. the account was deleted/disabled).
            // Wipe local auth state and send the user back to login - otherwise the app keeps
            // a stale "logged in" state and just drifts to the home page with no roles.
            this.handleUnauthorized();
          } else if (isForbidden) {
            console.log('Forbidden error, redirecting to home');
            this.router.navigateByUrl("")
          }
          if (!serviceConnectionIssue && !isUnauthorized && !isForbidden) {
            this.timestamp = new Date().toLocaleString();
            if (error.status !== 0) {
              HttpErrorInterceptor.toastService.addToast(!isWarning, this.timestamp, this.message, this.debugMessage, this.cause);
            }
          }
          return throwError(errorMessage);
        })
      );

  }

  private handlingUnauthorized = false;

  private handleUnauthorized(): void {
    const loginPath = '/' + CrystalClearFrontUrl.LOGIN.path;
    // Already on the login page (e.g. a failed login attempt) - nothing to clear/redirect.
    if (this.router.url.split('?')[0] === loginPath) {
      return;
    }
    // Several requests (e.g. a WS reconnect handshake plus an in-flight REST call) can 401
    // within the same tick once a session dies - only act on the first one so the user sees
    // a single explanation instead of the redirect firing repeatedly.
    if (this.handlingUnauthorized) {
      return;
    }
    this.handlingUnauthorized = true;

    // Resolve lazily to avoid a DI cycle (UserInfoStorageService depends on HttpClient).
    this.injector.get(UserInfoStorageService).removeToken();
    HttpErrorInterceptor.toastService.addToast(
      false,
      new Date().toLocaleString(),
      this.translateService.instant('interceptor.session-expired')
    );
    this.router.navigate([loginPath]).finally(() => {
      this.handlingUnauthorized = false;
    });
  }

}

export module HttpErrorInterceptor {
  export enum Type {
    NONE = 'none',
    USER_HAVE_TO_CHANGE_PASSWORD = 'error.message.user_have_to_change_password'
  }
}
